import { useCallback, useEffect, useState } from 'react'
import { Button, DatePicker, Divider, Form, Input, Modal, Select, Space, TableProps } from 'antd'
import { SearchOutlined, UndoOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'
import { apiGetListCompanies, apiGetListRegistrationRequests, apiUpdateListRegistrationRequests } from '../../api'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { ICompany } from '../../models/company.modal'
import { renderDateByUnix, renderStatus, renderType } from './method'
import { formatCurrency } from '../../utils/string-utils'
import { toastError, toastSuccess } from '../../utils/toast'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import moment from 'moment'

const rPhoneNumber = /^(0|84)?\d{9}$/

export default function KitSimRequest() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [registrationRequest, setRegistrationRequest] = useState<any>({})
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<any>({})
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const { account } = useSelector(authSelector)
    const [modalProcessing, setModalProcessing] = useState<any>({ isOpen: false, data: null })
    const [modalError, setModalError] = useState<any>({ isOpen: false, data: null })
    const [modalProcessed, setModalProcessed] = useState<any>({ isOpen: false, data: null })
    const [phoneCustomer, setPhoneCustomer] = useState('')
    const [description, setDescription] = useState('')

    const columns: TableProps<any>['columns'] = [
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'ownerBy',
            title: 'Nhân sự',
            dataIndex: 'ownerBy',
        },
        {
            key: 'serialSim',
            title: 'Serial Sim',
            dataIndex: 'serialSim',
        },
        {
            key: 'phoneCustomer',
            title: 'SĐT Khách hàng',
            dataIndex: 'phoneCustomer',
        },
        {
            key: 'dataPack',
            title: 'Gói cước',
            dataIndex: 'dataPack',
        },
        {
            key: 'price',
            title: 'Đã thanh toán',
            dataIndex: 'price',
            render: price => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {formatCurrency(price)}
                </span>
            ),
        },
        {
            key: 'startDate',
            title: 'Ngày yêu cầu',
            dataIndex: 'startDate',
            render: startDate => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {renderDateByUnix(startDate)}
                </span>
            ),
        },
        {
            key: 'type',
            title: 'Loại gói cước',
            dataIndex: 'type',
            render: type => renderType(type),
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderStatus(status),
        },
        {
            key: 'action',
            title: 'Cập nhật',
            render: (_, data: any) => renderAction(data),
        },
    ]

    const renderAction = (data: any) => {
        return (
            <Space>
                {account?.canUpdateListRegistrationRequests && (<>
                    {data.status === 'CREATED' ? (
                        <Button size='small'
                            style={{ color: '#2faac6', borderColor: '#2faac6' }}
                            onClick={() => setModalProcessing({ isOpen: true, data })}
                        >
                            Xử lý
                        </Button>
                    ) : data.status === 'PROCESSING' ? (
                        <Button size='small'
                            style={{ color: '#1d9a46', borderColor: '#1d9a46' }}
                            onClick={() => {
                                setPhoneCustomer(data.phoneCustomer || '')
                                setDescription(data.description || '')
                                setModalProcessed({ isOpen: true, data })
                            }}
                        >
                            Đã xử lý
                        </Button>
                    ) : <span>{data.description}</span>}
                    {(data.status === 'CREATED' || data.status === 'PROCESSING') && (
                        <Button size='small' danger onClick={() => {
                            setDescription('')
                            setModalError({ isOpen: true, data })
                        }}>
                            Huỷ
                        </Button>
                    )}
                </>)}
            </Space>
        )
    }

    const fetchRegistrationRequest = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp = await apiGetListRegistrationRequests({
                page: currentPage,
                size: pageSize,
                orderBy: '"created_at" desc',
                ...search,
            })
            if (resp.status === 200) {
                setRegistrationRequest(resp.data)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    const fetchCompanies = useCallback(async () => {
        try {
            const resp = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resp.data != null) {
                const options = resp.data.companies.map((item: ICompany) => ({
                    label: item.name,
                    value: item.code,
                }))
                setCompanyOptions(options)
            } else {
                setCompanyOptions([])
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchRegistrationRequest()
        fetchCompanies()
    }, [fetchRegistrationRequest, fetchCompanies])

    const handleSearch = ({ cpCode, serialSim, phoneCustomer, date }: any) => {
        if (!cpCode && !serialSim && !phoneCustomer && !date) return
        let startDate, endDate
        if (date) {
            startDate = date[0] ? moment(date[0]?.format('YYYY-MM-DD')).unix() : null
            endDate = date[1] ? moment(date[1]?.format('YYYY-MM-DD')).unix() : null
        }
        setSearch({ cpCode, serialSim, phoneCustomer, startDate, endDate })
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleUpdateRegistrationRequest = async (data: any) => {
        try {
            setIsLoading(true)
            const resp = await apiGetListRegistrationRequests({ page: 1, size: 1, condition: `id = ${data.id}` })
            if (resp.data.requests.length !== 1) {
                toastError('Không tìm thấy yêu cầu!')
                return
            }
            const request = resp.data.requests[0]
            if (request.status === data.status) {
                toastError('Yêu cầu đã được cập nhật trước đó!')
                return
            }
            await apiUpdateListRegistrationRequests({ requests: [data] })
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            await fetchRegistrationRequest()
            setIsLoading(false)
        }
    }

    const handleProcessing = async () => {
        await handleUpdateRegistrationRequest({
            ...modalProcessing?.data,
            ownerBy: account?.username || '',
            status: 'PROCESSING',
        })
        setModalProcessing({ isOpen: false, data: null })
    }

    const handleError = async () => {
        await handleUpdateRegistrationRequest({
            ...modalError?.data,
            ownerBy: account?.username || '',
            status: 'ERROR',
            description,
        })
        setModalError({ isOpen: false, data: null })
    }

    const handleSuccess = async () => {
        if (phoneCustomer.trim() !== '' && !rPhoneNumber.test(phoneCustomer)) {
            toastError('SĐT Khách hàng không hợp lệ!')
            return
        }
        await handleUpdateRegistrationRequest({
            ...modalProcessed.data,
            ownerBy: account?.username || '',
            status: 'SUCCESS',
            description,
            phoneCustomer,
        })
        setModalProcessed({ isOpen: false, data: null })
        toastSuccess('Thêm đấu nối mới thành công!')
    }

    const handleFail = async () => {
        if (phoneCustomer.trim() !== '' && !rPhoneNumber.test(phoneCustomer)) {
            toastError('SĐT Khách hàng không hợp lệ!')
            return
        }
        await handleUpdateRegistrationRequest({
            ...modalProcessed.data,
            ownerBy: account?.username || '',
            status: 'FAIL',
            description,
            phoneCustomer,
        })
        setModalProcessed({ isOpen: false, data: null })
    }

    return (
        <div>
            <Form form={formSearch} onFinish={handleSearch}>
                <div style={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 20 }}>
                    <Form.Item name='cpCode' label='Công ty'>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn công ty"
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name='phoneCustomer' label='SĐT Khách hàng'>
                        <Input autoComplete='off' />
                    </Form.Item>
                    <Form.Item name='serialSim' label='SerialSim'>
                        <Input autoComplete='off' />
                    </Form.Item>
                    <Form.Item name="date" label="Ngày đấu nối">
                        <DatePicker.RangePicker
                            format="YYYY-MM-DD"
                            picker="date" />
                    </Form.Item>
                    <Space style={{ marginBottom: 24 }}>
                        <Button type='primary' htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                        <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                    </Space>
                </div>
            </Form>
            <CustomTable
                namePage='yêu cầu'
                columns={columns}
                dataSource={registrationRequest.requests}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={registrationRequest.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                title='Xử lý yêu cầu'
                open={modalProcessing.isOpen}
                footer={null}
                onCancel={() => setModalProcessing({ isOpen: false, data: null })}>
                <Divider />
                Cập nhật trạng thái <b>đang xử lý</b> cho <b>{account?.username}</b>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={handleProcessing} style={{ color: 'white', background: '#1d9a46' }}>Xử lý</Button>
                </div>
            </Modal>
            <Modal
                title='Huỷ yêu cầu'
                open={modalError.isOpen}
                footer={null}
                onCancel={() => setModalError({ isOpen: false, data: null })}>
                <Divider />
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>Mô tả:</div>
                    <Input
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={handleError} style={{ color: 'white', background: '#c93c3c' }}>Huỷ</Button>
                </div>
            </Modal>
            <Modal
                title='Hoàn tất yêu cầu'
                open={modalProcessed.isOpen}
                footer={null}
                onCancel={() => setModalProcessed({ isOpen: false, data: null })}>
                <Divider />
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>SĐT Khách hàng</div>
                    <Input
                        value={phoneCustomer}
                        onChange={e => setPhoneCustomer(e.target.value)}
                    />
                </div>
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>Mô tả</div>
                    <Input
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={handleSuccess} style={{ color: 'white', background: '#9b9b05' }}>Thành công</Button>
                        <Button onClick={handleFail} style={{ color: 'white', background: '#c93c3c' }}>Thất bại</Button>
                    </Space>
                </div>
            </Modal>
        </div>
    )
}
